import { supabase, isSupabaseConfigured, requireSupabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import type {
    Ticket,
    TicketStats,
    TicketTrends,
    CreateTicketInput,
    TicketStatus,
    TicketUrgency,
} from "@/types/ticket";

/**
 * Ticket Service — CRUD + Realtime for practice tickets
 */

export async function getTickets(
    practiceId: string,
    filters?: {
        status?: TicketStatus[];
        urgency?: TicketUrgency;
        assigneeId?: string;
        search?: string;
    },
): Promise<Ticket[]> {
    if (!isSupabaseConfigured()) return [];

    try {
        let query = supabase
            .from("tickets")
            .select("*")
            .eq("practice_id", practiceId)
            .order("created_at", { ascending: false });

        if (filters?.status?.length) {
            query = query.in("status", filters.status);
        }
        if (filters?.urgency) {
            query = query.eq("urgency", filters.urgency);
        }
        if (filters?.assigneeId) {
            query = query.eq("assigned_to", filters.assigneeId);
        }
        if (filters?.search) {
            query = query.or(
                `subject.ilike.%${filters.search}%,content.ilike.%${filters.search}%,patient_name.ilike.%${filters.search}%`,
            );
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data || []).map(mapTicket);
    } catch (err) {
        logger.error("[Tickets] Laden fehlgeschlagen:", err);
        return [];
    }
}

export async function getTicketById(ticketId: string): Promise<Ticket | null> {
    const sb = requireSupabase();
    const { data, error } = await sb.from("tickets").select("*").eq("id", ticketId).single();

    if (error || !data) return null;
    return mapTicket(data);
}

export async function createTicket(
    practiceId: string,
    input: CreateTicketInput,
): Promise<Ticket | null> {
    const sb = requireSupabase();
    const { data, error } = await sb
        .from("tickets")
        .insert({
            practice_id: practiceId,
            subject: input.subject || input.title || null,
            type: input.type,
            urgency: input.urgency,
            content: input.content || input.description || "",
            patient_id: input.patientId || null,
            patient_name: input.patientName || null,
            assigned_to: input.assignedTo || input.assigneeId || null,
            status: "open",
        })
        .select()
        .single();

    if (error) {
        logger.error("[Tickets] Erstellen fehlgeschlagen:", error);
        return null;
    }
    return mapTicket(data);
}

export async function updateTicketStatus(
    ticketId: string,
    status: TicketStatus,
    resolution?: string,
): Promise<boolean> {
    const sb = requireSupabase();
    const update: Record<string, unknown> = { status };
    if (status === "resolved" && resolution) {
        update.resolution = resolution;
        update.resolved_at = new Date().toISOString();
    }

    const { error } = await sb.from("tickets").update(update).eq("id", ticketId);

    if (error) {
        logger.error("[Tickets] Status-Update fehlgeschlagen:", error);
        return false;
    }
    return true;
}

export async function assignTicket(ticketId: string, assigneeId: string): Promise<boolean> {
    const sb = requireSupabase();
    const { error } = await sb
        .from("tickets")
        .update({ assigned_to: assigneeId, status: "in_progress" })
        .eq("id", ticketId);

    if (error) {
        logger.error("[Tickets] Zuweisung fehlgeschlagen:", error);
        return false;
    }
    return true;
}

export async function updateTicketUrgency(
    ticketId: string,
    urgency: TicketUrgency,
): Promise<boolean> {
    const sb = requireSupabase();
    const { error } = await sb.from("tickets").update({ urgency }).eq("id", ticketId);
    if (error) {
        logger.error("[Tickets] Urgency-Update fehlgeschlagen:", error);
        return false;
    }
    return true;
}

export async function resolveTicket(
    ticketId: string,
    resolution: string,
    resolvedBy?: string,
): Promise<boolean> {
    const sb = requireSupabase();
    const update: Record<string, unknown> = {
        status: "resolved",
        resolution,
        resolved_at: new Date().toISOString(),
    };
    if (resolvedBy) update.resolved_by = resolvedBy;

    const { error } = await sb.from("tickets").update(update).eq("id", ticketId);
    if (error) {
        logger.error("[Tickets] Resolve fehlgeschlagen:", error);
        return false;
    }
    return true;
}

export async function deleteTicket(ticketId: string): Promise<boolean> {
    const sb = requireSupabase();
    const { error } = await sb.from("tickets").delete().eq("id", ticketId);
    if (error) {
        logger.error("[Tickets] Löschen fehlgeschlagen:", error);
        return false;
    }
    return true;
}

export async function getTicketStats(practiceId: string): Promise<TicketStats> {
    const defaultStats: TicketStats = {
        open: 0,
        inProgress: 0,
        resolved: 0,
        total: 0,
    };
    if (!isSupabaseConfigured()) return defaultStats;

    try {
        const { data, error } = await supabase
            .from("tickets")
            .select("status, urgency, resolved_at, created_at")
            .eq("practice_id", practiceId);

        if (error || !data) return defaultStats;

        const stats = { ...defaultStats };

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayStr = todayStart.toISOString();

        for (const t of data) {
            if (t.status === "open") stats.open++;
            if (t.status === "in_progress") stats.inProgress++;
            if (t.status === "pending_patient")
                stats.waitingForPatient = (stats.waitingForPatient || 0) + 1;
            if (
                t.urgency === "red" &&
                (t.status === "open" || t.status === "in_progress")
            ) {
                stats.highPriority = (stats.highPriority || 0) + 1;
            }
            if (t.status === "resolved") {
                stats.resolved++;
                // Count resolved today
                if (t.resolved_at && t.resolved_at >= todayStr) {
                    stats.resolvedToday = (stats.resolvedToday || 0) + 1;
                }
            }
        }
        stats.total = data.length;

        return stats;
    } catch {
        return defaultStats;
    }
}

/**
 * Compute ticket trend KPIs over the last 4 weeks vs. the 4 weeks before.
 * All computation is client-side to avoid a new DB function/migration.
 * Returns sparkline (28 daily buckets) + deltas for 3 KPIs.
 */
export async function getTicketTrends(practiceId: string): Promise<TicketTrends> {
    const empty: TicketTrends = {
        critical: { count: 0, delta: 0, sparkline: [] },
        avgResolutionMinutes: { count: 0, delta: 0, sparkline: [] },
        afterHours: { count: 0, delta: 0, sparkline: [] },
        period: "Letzte 4 Wochen",
    };
    if (!isSupabaseConfigured()) return empty;

    try {
        const now = new Date();
        const DAYS_WINDOW = 56; // 8 weeks total (4 current + 4 comparison)
        const since = new Date(now.getTime() - DAYS_WINDOW * 86_400_000);

        const { data, error } = await supabase
            .from("tickets")
            .select("urgency, status, created_at, resolved_at")
            .eq("practice_id", practiceId)
            .gte("created_at", since.toISOString())
            .order("created_at", { ascending: true });

        if (error || !data) return empty;

        // Split into current (0-27) and previous (28-55) windows
        const midpoint = new Date(now.getTime() - 28 * 86_400_000);

        // Helper: day index relative to `since`
        const dayOf = (iso: string) =>
            Math.floor((new Date(iso).getTime() - since.getTime()) / 86_400_000);

        // Sparkline buckets (only current 28 days)
        const criticalByDay = new Array(28).fill(0);
        const resolutionByDay = new Array(28).fill(0);
        const resolutionCountByDay = new Array(28).fill(0);
        const afterHoursByDay = new Array(28).fill(0);

        let critCurrent = 0;
        let critPrev = 0;
        let afterCurrent = 0;
        let afterPrev = 0;
        const resMinsCurrent: number[] = [];
        const resMinsPrev: number[] = [];

        for (const t of data) {
            const created = new Date(t.created_at);
            const isCurrent = created >= midpoint;
            const isCritical = t.urgency === "red";
            const hour = created.getHours();
            const isAfterHours = hour >= 18 || hour < 7;

            if (isCurrent) {
                if (isCritical) critCurrent++;
                if (isAfterHours) afterCurrent++;
                // Sparkline data (offset by 28 since dayOf starts from `since`)
                const sparkIdx = dayOf(t.created_at) - 28;
                if (sparkIdx >= 0 && sparkIdx < 28) {
                    if (isCritical) criticalByDay[sparkIdx]++;
                    if (isAfterHours) afterHoursByDay[sparkIdx]++;
                }
            } else {
                if (isCritical) critPrev++;
                if (isAfterHours) afterPrev++;
            }

            // Resolution time
            if (t.resolved_at && (t.status === "resolved" || t.status === "closed")) {
                const mins =
                    (new Date(t.resolved_at).getTime() - created.getTime()) / 60_000;
                if (mins > 0 && mins < 14_400) {
                    // sanity: < 10 days
                    if (isCurrent) {
                        resMinsCurrent.push(mins);
                        const sparkIdx = dayOf(t.created_at) - 28;
                        if (sparkIdx >= 0 && sparkIdx < 28) {
                            resolutionByDay[sparkIdx] += mins;
                            resolutionCountByDay[sparkIdx]++;
                        }
                    } else {
                        resMinsPrev.push(mins);
                    }
                }
            }
        }

        const avgCurrent =
            resMinsCurrent.length > 0
                ? resMinsCurrent.reduce((a, b) => a + b, 0) / resMinsCurrent.length
                : 0;
        const avgPrev =
            resMinsPrev.length > 0
                ? resMinsPrev.reduce((a, b) => a + b, 0) / resMinsPrev.length
                : 0;

        const pctDelta = (cur: number, prev: number) =>
            prev === 0 ? (cur > 0 ? 100 : 0) : Math.round(((cur - prev) / prev) * 100);

        // Resolution sparkline: daily averages in minutes
        const resSparkline = resolutionByDay.map((total, i) =>
            resolutionCountByDay[i] > 0 ? Math.round(total / resolutionCountByDay[i]) : 0,
        );

        return {
            critical: {
                count: critCurrent,
                delta: pctDelta(critCurrent, critPrev),
                sparkline: criticalByDay,
            },
            avgResolutionMinutes: {
                count: Math.round(avgCurrent),
                delta: pctDelta(avgCurrent, avgPrev),
                sparkline: resSparkline,
            },
            afterHours: {
                count: afterCurrent,
                delta: pctDelta(afterCurrent, afterPrev),
                sparkline: afterHoursByDay,
            },
            period: "Letzte 4 Wochen",
        };
    } catch (err) {
        logger.error("[Tickets] Trends-Berechnung fehlgeschlagen:", err);
        return empty;
    }
}

/**
 * Subscribe to realtime ticket changes
 */
export function subscribeToTickets(practiceId: string, onUpdate: (ticket: Ticket) => void) {
    const channel = supabase
        .channel(`tickets:${practiceId}`)
        .on(
            "postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "tickets",
                filter: `practice_id=eq.${practiceId}`,
            },
            (payload) => {
                if (payload.new) {
                    onUpdate(mapTicket(payload.new as Record<string, unknown>));
                }
            },
        )
        .subscribe();

    return () => {
        supabase.removeChannel(channel);
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapTicket(row: any): Ticket {
    return {
        id: row.id,
        practiceId: row.practice_id,
        patientName: row.patient_name || "",
        title: row.title || row.subject || "",
        description: row.description || row.content || undefined,
        assigneeId: row.assignee_id || row.assigned_to || undefined,
        assigneeName: row.assignee_name || row.assigned_to_name || undefined,
        assignedTo: row.assigned_to || row.assignee_id || undefined,
        assignedToName: row.assigned_to_name || row.assignee_name || undefined,
        type: row.type,
        subject: row.subject || row.title || undefined,
        content: row.content || row.description || "",
        urgency: row.urgency || "green",
        status: row.status || "open",
        patientId: row.patient_id || undefined,
        resolvedAt: row.resolved_at || undefined,
        createdAt: row.created_at,
        updatedAt: row.updated_at || row.created_at,
    };
}

/**
 * Get top open tickets sorted by urgency (red first), limited.
 * Used by the dashboard worklist.
 */
export async function getTopOpenTickets(
    practiceId: string,
    limit = 10,
): Promise<Ticket[]> {
    if (!isSupabaseConfigured()) return [];

    try {
        const { data, error } = await supabase
            .from("tickets")
            .select("*")
            .eq("practice_id", practiceId)
            .in("status", ["open", "in_progress", "waiting_for_doctor", "pending_doctor"])
            .limit(Math.max(limit * 3, 30));

        if (error) throw error;

        const mapped = (data || []).map(mapTicket);
        // Sort: red first, then yellow, then green
        const urgencyOrder: Record<string, number> = { red: 0, yellow: 1, green: 2 };
        return mapped
            .sort((a, b) => {
                const urgencyDelta = (urgencyOrder[a.urgency] ?? 3) - (urgencyOrder[b.urgency] ?? 3);
                if (urgencyDelta !== 0) return urgencyDelta;
                return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            })
            .slice(0, limit);
    } catch (err) {
        logger.error("[Tickets] getTopOpenTickets fehlgeschlagen:", err);
        return [];
    }
}
